import { useQuery } from "@tanstack/react-query";
import { PropsWithChildren, createContext, useCallback, useContext, useState } from "react";
import { FormattedMessage } from "react-intl";
import { AuthContext, IAuthContext } from "react-oauth2-code-pkce";
import { useSearchParams } from "react-router";

import { Creator, getRanaApiV1 } from "@nens/rana/api-v1";
import { setHCCAccessToken, setHCCBaseUrl } from "@nens/threedi/openapi-client";

import { RanaInfoBox } from "../../lib/components/rana-info-box";
import { useFrontendSetting } from "../../lib/hooks/use-frontend-settings";
import { storeTenantAndOrganisation } from "../../lib/links-between-sites";
import { SystemStatusPage } from "../../lib/rana-system-status/system-status-page";
import { ACCOUNT_HAS_NO_ACCESS_SESSION_KEY, getLogoutRedirect } from "../auth-constants";
import { AuthLoader } from "../components/auth-loader";
import { ClearlyAuthRedirect } from "../components/clearly-auth-redirect";
import { useUserPutMutationQuery } from "../hooks/invite-or-create-account";
import { useClearlySubscription } from "../hooks/use-clearly-subscription";
import { savePublicUserInStorage } from "../util/public-user-in-storage";

import { AuthProviderForIdentityProvider } from "./auth-provider-for-tenant-and-provider";

// This is where the normal authentication logic for Rana is handled.

// There are TWO types of authentication supported:
// 1. Normal Authentication (for regular users)
// 2. Shared Publication Authentication (via special links) (see tenant-provider-public.tsx)

// ==========================
// 1. NORMAL AUTHENTICATION:
// ==========================

// Given a tenant, we either:
// - Retrieve the user's associated identity provider, OR
// - Prompt the user to select one.

// Once we have the identity provider, we use `AuthProviderForIdentityProvider`
// to wrap the app with a `react-oauth2-code-pkce` AuthProvider, configured
// to perform the OAuth2 flow with AWS Cognito.

// Inside this AuthProvider, we render `NormalTenantProviderInner`, which:
// - Uses the auth context from the parent AuthProvider
// - Provides to the app:
//   - Whether the user is logged in
//   - A logout function
//   - Basic user details (e.g. name, email)
// - Also sets the access token in Rana’s API client,
//   so that all subsequent API requests are authenticated.
// - Performs a PUT to the user endpoint to update user details
//   and to accept any invitations.
// - Fetches the tenant to check that the user has access to it.
// - Finally, renders the app's children.

// If at any point something is wrong (e.g. user doesn't have access to the tenant),
// the user is logged out and redirected to the login page.

// STAT

export const useTenantQuery = (isDisabled?: boolean) => {
  return useQuery(
    getRanaApiV1().getQueryOptions({
      queryKey: ["tenant", "detail"],
      queryFn: async () => getRanaApiV1().TenantsApi.tenantRetrieve({}),
      enabled: !isDisabled,
    })
  );
};

export interface TenantUser {
  id: string;
  email: string;
  givenName: string;
  familyName: string;
}

export type AuthInfo =
  | {
      identityProvider: string;
      tenant: string;
      logOut: () => void;
      user: TenantUser;
      isPublic: false;
      authorizationHeader: string;
    }
  | {
      tenant: string;
      isPublic: true;
      user: Creator;
      setPublicUser: (publicUser: Creator) => void;
      authorizationHeader: string;
    };

export const TenantContext = createContext<AuthInfo>({
  identityProvider: "",
  tenant: "",
  user: null as unknown as TenantUser,
  logOut: () => undefined,
  isPublic: false,
  authorizationHeader: "",
});

export const INVITATION_ID_KEY = "invitationId";

export function chosenIdentityProviderKey(tenant: string) {
  return `rana_localstorage_key_${tenant}_identity_provider`;
}

interface TenantProviderProps extends PropsWithChildren {
  tenant: string;
  notLoggedInRoutes: (setIdentityProvider: (identityProvider: string) => void) => JSX.Element;
  afterLoginLogic?: (tenant: string, logout: () => void) => JSX.Element | null;
  oauthRedirectUrlPrefix?: string;
}
// This provider keeps track of the current tenant, and also the last chosen
// identity provider (each tenant can have different identity providers).

// If there is no identity provider chosen, show a screen to choose one.

// A 'tenantLogout' function is provided to clear stored information. It should
// be called as part of the logout functionality.

export function TenantProvider(props: TenantProviderProps) {
  const { children, tenant, notLoggedInRoutes, afterLoginLogic } = props;

  // Store separately for each tenant, for users who log in to multiple tenants
  // with potentially different identity providers.
  const [searchParams] = useSearchParams();
  const defaultIdentityProvider =
    searchParams.get("provider") ||
    window.localStorage.getItem(chosenIdentityProviderKey(tenant)) ||
    null;

  const [identityProvider, setIdentityProvider] = useState<string | null>(defaultIdentityProvider);
  const [loggingOut, setLoggingOut] = useState(false);

  const tenantLogout = useCallback(() => {
    setLoggingOut(true);
    setIdentityProvider(null);
    window.localStorage.removeItem(chosenIdentityProviderKey(tenant));
  }, [setIdentityProvider, tenant]);

  // Don't render anything right before being redirected away.
  if (loggingOut) return null;

  if (!identityProvider) {
    return notLoggedInRoutes(setIdentityProvider);
  }

  return (
    <AuthProviderForIdentityProvider
      tenant={tenant}
      identityProvider={identityProvider}
      oauthRedirectUrlPrefix={props.oauthRedirectUrlPrefix}
    >
      <NormalTenantProviderInner
        tenant={tenant}
        identityProvider={identityProvider}
        tenantLogout={tenantLogout}
        afterLoginLogic={afterLoginLogic}
      >
        {children}
      </NormalTenantProviderInner>
    </AuthProviderForIdentityProvider>
  );
}

interface NormalTenantProviderInnerProps extends PropsWithChildren {
  tenant: string;
  identityProvider: string;
  tenantLogout: () => void;
  afterLoginLogic?: (tenant: string, logout: () => void) => JSX.Element | null;
  oauthRedirectUrlPrefix?: string;
}

function NormalTenantProviderInner({
  children,
  identityProvider,
  tenant,
  tenantLogout,
  afterLoginLogic,
  oauthRedirectUrlPrefix,
}: NormalTenantProviderInnerProps) {
  const authContext = useContext<IAuthContext>(AuthContext);
  const {
    logoutUrl,
    defaultClientId,
    nativeClientId,
    isSuccess: isFrontendSettingsSuccess,
    isError: isFrontendSettingsError,
    hccUrl,
  } = useFrontendSetting();

  const loggedIn = !!(authContext.token && authContext.idToken);
  const authorizationHeader = loggedIn ? `Bearer ${authContext.token}` : "";

  const logOut = useCallback(() => {
    // Forget which tenant and organisation we were logged in to
    storeTenantAndOrganisation("", []);

    if (!isFrontendSettingsSuccess) throw Error("Frontend settings not loaded");
    // Forget tenant and identity provider
    tenantLogout();

    // Remove Django session cookie of the HCC API, if present
    document.cookie = "sessionid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

    // Forget tokens
    authContext.logOut();
    const clientId = identityProvider === "rana" ? nativeClientId : defaultClientId;
    // Redirect to Cognito for logout
    window.location.assign(
      `${logoutUrl}?client_id=${clientId}&logout_uri=${encodeURIComponent(
        getLogoutRedirect(oauthRedirectUrlPrefix)
      )}`
    );
  }, [
    authContext,
    defaultClientId,
    identityProvider,
    isFrontendSettingsSuccess,
    logoutUrl,
    nativeClientId,
    tenantLogout,
    oauthRedirectUrlPrefix,
  ]);

  const givenName: string = authContext.idTokenData?.given_name ?? "";
  const familyName: string = authContext.idTokenData?.family_name ?? "";

  const id: string = authContext.tokenData?.sub ?? "";
  const email: string = authContext.idTokenData?.email ?? "";
  const user =
    authContext.tokenData && authContext.idTokenData
      ? {
          id,
          email,
          givenName,
          familyName,
        }
      : undefined;
  if (user) savePublicUserInStorage(user.givenName, user.familyName, user.email);

  if (loggedIn) {
    // Set the token and tenant on the API object.
    // This can safely be set every render.
    getRanaApiV1().setAccessToken(authorizationHeader);
    setHCCAccessToken(authContext.token); // Adds Bearer itself, so don't add it here.
    if (hccUrl) {
      setHCCBaseUrl(hccUrl);
    }
    getRanaApiV1().setTenantId(tenant);
  } else {
    getRanaApiV1().setAccessToken("");
    getRanaApiV1().setTenantId("");
    setHCCAccessToken("");
  }

  /* When we are logged in, we PUT the user details to the backend. This has
     two purposes:
     - Updated user details (like name) get updated in the backend
     - Accepted invitations take effect, the new account gets roles, etc.

     Because of the latter, this PUT must happen and be finished before
     we check that the user has access to this tenant. So we keep that in
     a state.

     The user PUT request may error, in which case we show the error to the user, expections:

     1. If the PUT request errors and the tenant query does not. Then the user likely has a service desk role,
     then they are authorized to read but don't actually have an account in said tenant.
     They can't edit anything in the tenant because the PUT wasn't successful but that is expected given their role.

     2. In the admin pages we want to forgo the automatic PUT logic alltogether, because
     when don't want to be added to a tenant just by visiting the admin page, and the PUT request can cause issues with users that have
     access to the admin page but don't have the correct identity provider or other details to be able to successfully complete the PUT request.
   */
  const isLoggedIn = !!(loggedIn && user);

  const adminPath = `/${tenant}/admin`;
  const isAdminPath = window.location.pathname.startsWith(adminPath);

  const invitationId = window.sessionStorage.getItem(INVITATION_ID_KEY);
  const enabled = !isAdminPath;
  const userPutQuery = useUserPutMutationQuery({
    id,
    givenName,
    familyName,
    email,
    invitationId,
    tenant,
    enabled: isLoggedIn && enabled,
  });

  const userPutHappened = userPutQuery.isSuccess || userPutQuery.isError || !enabled;

  // When we are logged in, after the PUT, fetch the tenant. If we can't get it, we don't have access.
  const tenantQuery = useTenantQuery(!isLoggedIn || !userPutHappened);

  const clearlySubscription = useClearlySubscription({
    user,
    identityProvider,
  });

  if (authContext.error) {
    return <SystemStatusPage variant="network-error" />;
  }

  if (userPutQuery.isBusy || tenantQuery.isLoading) {
    return <AuthLoader />;
  }

  // Is redirecting away, don't render anything
  if (!isLoggedIn) return null;

  const isUserError = userPutQuery.isError;
  const isAuthorized = userPutHappened && tenantQuery.isSuccess;
  const isNotAuthorized = userPutHappened && tenantQuery.isError;

  if (tenantQuery.isSuccess && tenantQuery.data) {
    // Set the tenant and its 3Di organisations for the HCC frontends
    storeTenantAndOrganisation(tenantQuery.data.id, tenantQuery.data.threediOrganisations);
  }

  if (isUserError) {
    if (clearlySubscription.isClearly) {
      return <ClearlyAuthRedirect logOut={logOut} {...clearlySubscription} />;
    }

    if (!isAuthorized) {
      return (
        <RanaInfoBox
          title={<FormattedMessage id="heading.Login failed" defaultMessage="Login failed" />}
          errors={userPutQuery.errors}
          button={{
            text: <FormattedMessage id="button.signout" defaultMessage="Sign out" />,
            onClick: logOut,
          }}
        />
      );
    }
  }

  if (isNotAuthorized) {
    if (isFrontendSettingsSuccess) {
      window.sessionStorage.setItem(ACCOUNT_HAS_NO_ACCESS_SESSION_KEY, "true");
      logOut();
    } else if (isFrontendSettingsError) {
      // The chances of this happening are astronomically small so i'm not gonna bother with a proper error page.
      return "You do not have access to this tenant but something went wrong with getting the logout url so we cannot automatically log you out. Please try again later.";
    }
    return null;
  }

  if (afterLoginLogic) {
    const afterLoginElement = afterLoginLogic(tenant, logOut);
    if (afterLoginElement) return afterLoginElement;
  }

  const tenantContext: AuthInfo = {
    identityProvider,
    tenant,
    logOut,
    user,
    isPublic: false,
    authorizationHeader,
  };

  return (
    <TenantContext.Provider value={tenantContext}>
      {isAuthorized ? children : null}
    </TenantContext.Provider>
  );
}
